import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { RateCorrectionsEditorContainerOwnProps } from '../../containers/RateCorrectionsEditorContainer/RateCorrectionsEditorContainer';
import { defaultCorrectionEditorState } from '../../reducers/rateCorrectionsEditor';
import { GdBidModifierType } from '../../typings/graphql-grid';
import { GroupId } from '../../typings/groups';
import {
    DeviceRateCorrectionType,
    INVENTORY_AD_TYPE_CORRECTIONS,
    INVENTORY_VIDEO_CORRECTIONS,
    InventoryRateCorrectionType,
    RateCorrectionEditorState,
    RateCorrectionType
} from '../../typings/ratesCorrections';
import { RateCorrectionEditorsState, State } from '../../typings/state';

export const selectEnabledMobileDevices = (
    containerProps: RateCorrectionsEditorContainerOwnProps
): Array<DeviceRateCorrectionType> => {
    const device =
        containerProps.rateCorrectionsConstants && containerProps.rateCorrectionsConstants[RateCorrectionType.device];

    if (device !== undefined) {
        return device.osTypes;
    }

    return [];
};

export const selectEnabledInventoryTypes = (
    containerProps: RateCorrectionsEditorContainerOwnProps
): Array<InventoryRateCorrectionType> => {
    const inventory =
        containerProps.rateCorrectionsConstants &&
        containerProps.rateCorrectionsConstants[RateCorrectionType.inventory];

    if (inventory !== undefined) {
        return inventory.inventoryTypes;
    }

    return [];
};

export const selectRateCorrectionsEditorByState = (
    state: RateCorrectionEditorsState,
    id: GroupId
): RateCorrectionEditorState => {
    return state[id] || defaultCorrectionEditorState;
};

export const selectEnabledDevices = createSelector(
    (containerProps: RateCorrectionsEditorContainerOwnProps) => containerProps.enabledRateCorrections,
    selectEnabledMobileDevices,
    (
        enabledRateCorrections: Array<GdBidModifierType>,
        osTypes: Array<DeviceRateCorrectionType>
    ): Array<DeviceRateCorrectionType> => {
        const devicesArray: Array<DeviceRateCorrectionType> = [];

        if (enabledRateCorrections.includes(GdBidModifierType.MOBILE_MULTIPLIER)) {
            devicesArray.push(DeviceRateCorrectionType.allMobile);
            devicesArray.push(...osTypes);
        }

        if (enabledRateCorrections.includes(GdBidModifierType.DESKTOP_MULTIPLIER)) {
            devicesArray.push(DeviceRateCorrectionType.desktops);
        }

        return devicesArray;
    }
);

export const selectEnabledInventoryItems = createSelector(
    (containerProps: RateCorrectionsEditorContainerOwnProps) => containerProps.enabledRateCorrections,
    selectEnabledInventoryTypes,
    (
        enabledRateCorrections: Array<GdBidModifierType>,
        inventoryTypes: Array<InventoryRateCorrectionType>
    ): Array<InventoryRateCorrectionType> => {
        let inventoryArray: Array<InventoryRateCorrectionType> = [];

        if (enabledRateCorrections.includes(GdBidModifierType.INVENTORY_MULTIPLIER)) {
            const inventoryType = new Set(INVENTORY_VIDEO_CORRECTIONS);
            inventoryArray = inventoryArray.concat(inventoryTypes.filter(type => inventoryType.has(type)));
        }

        if (enabledRateCorrections.includes(GdBidModifierType.AD_TYPE_MULTIPLIER)) {
            const adType = new Set(INVENTORY_AD_TYPE_CORRECTIONS);
            inventoryArray = inventoryArray.concat(inventoryTypes.filter(type => adType.has(type)));
        }

        return inventoryArray;
    }
);

export const selectRateCorrectionsEditorValue = createCachedSelector(
    (state: State) => state.rateCorrectionsEditor,
    (state: State, editorId: string) => editorId,
    (editors: RateCorrectionEditorsState, editorId: string): RateCorrectionEditorState => {
        return editors[editorId] || defaultCorrectionEditorState;
    }
)((state: State, editorId: string) => editorId);
